<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import Icon from './Icon.vue'
import ScrollAnimate from './ScrollAnimate.vue'
import { useI18n } from '../composables/useI18n'

const { t } = useI18n()

const active = ref('mcp')
const copied = ref<string | null>(null)
const copyError = ref<string | null>(null)
let copyTimer: ReturnType<typeof setTimeout> | null = null
let copyErrorTimer: ReturnType<typeof setTimeout> | null = null

interface Tab {
  id: string
  label: string
}

const tabs = computed<Tab[]>(() => [
  { id: 'mcp', label: t('getStarted.tabs.mcp') as string },
  { id: 'pi', label: t('getStarted.tabs.pi') as string },
  { id: 'python', label: t('getStarted.tabs.python') as string },
  { id: 'desktop', label: t('getStarted.tabs.desktop') as string },
])

type SnippetKey = 'mcp' | 'pi' | 'python'

const snippets = computed<Record<SnippetKey, string>>(() => ({
  mcp: t('getStarted.snippets.mcp') as string,
  pi: t('getStarted.snippets.pi') as string,
  python: t('getStarted.snippets.python') as string,
}))

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function highlightPython(code: string): string {
  const keywords = new Set([
    'and', 'as', 'assert', 'break', 'class', 'continue', 'def', 'del', 'elif', 'else',
    'except', 'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda',
    'nonlocal', 'not', 'or', 'pass', 'raise', 'return', 'try', 'while', 'with', 'yield',
    'True', 'False', 'None',
  ])

  return code.split('\n').map((line) => {
    if (!line.trim()) {
      return escapeHtml(line)
    }

    const trimmed = line.trimStart()
    if (trimmed.startsWith('pip install')) {
      return `<span class="text-secondary">${escapeHtml(line)}</span>`
    }

    let out = ''
    let pos = 0
    const len = line.length

    while (pos < len) {
      const ch = line[pos]

      // Whitespace
      if (ch === ' ' || ch === '\t') {
        const start = pos
        while (pos < len && (line[pos] === ' ' || line[pos] === '\t')) {
          pos++
        }
        out += escapeHtml(line.slice(start, pos))
        continue
      }

      // Comment
      if (ch === '#') {
        out += `<span class="text-base-content/50">${escapeHtml(line.slice(pos))}</span>`
        break
      }

      // String
      if (ch === '"' || ch === "'") {
        const quote = ch
        let end = pos + 1
        while (end < len) {
          if (line[end] === '\\') {
            end += 2
            continue
          }
          if (line[end] === quote) {
            end++
            break
          }
          end++
        }
        out += `<span class="text-success">${escapeHtml(line.slice(pos, end))}</span>`
        pos = end
        continue
      }

      // Number
      if (/\d/.test(ch) || (ch === '.' && pos + 1 < len && /\d/.test(line[pos + 1]))) {
        const start = pos
        while (pos < len && /[\d.]/.test(line[pos])) {
          pos++
        }
        out += `<span class="text-warning">${escapeHtml(line.slice(start, pos))}</span>`
        continue
      }

      // Identifier / keyword
      if (/[a-zA-Z_]/.test(ch)) {
        const start = pos
        while (pos < len && /[a-zA-Z0-9_]/.test(line[pos])) {
          pos++
        }
        const word = line.slice(start, pos)
        if (keywords.has(word)) {
          out += `<span class="text-info">${escapeHtml(word)}</span>`
        } else {
          out += escapeHtml(word)
        }
        continue
      }

      // Operator / punctuation
      if (/[=+\-*/%<>!&|:;,.()\[\]{}]/.test(ch)) {
        out += `<span class="text-base-content/60">${escapeHtml(ch)}</span>`
        pos++
        continue
      }

      // Fallback
      out += escapeHtml(ch)
      pos++
    }

    return out
  }).join('\n')
}

const pythonDisplay = computed(() => highlightPython(snippets.value.python))

function tabId(id: string) {
  return `tab-${id}`
}

function panelId(id: string) {
  return `panel-${id}`
}

function copyButtonText(id: string): string {
  if (copied.value === id) return t('getStarted.copied') as string
  if (copyError.value === id) return t('getStarted.copyError') as string
  return t('getStarted.copy') as string
}

function copyAriaLabel(id: string): string {
  if (copied.value === id) return t('getStarted.ariaCopied') as string
  if (copyError.value === id) return t('getStarted.ariaCopyError') as string
  return t('getStarted.ariaCopy') as string
}

async function copy(id: SnippetKey) {
  const text = snippets.value[id] ?? ''
  if (!text) return

  try {
    copyError.value = null
    if (copyErrorTimer) {
      clearTimeout(copyErrorTimer)
      copyErrorTimer = null
    }
    await navigator.clipboard.writeText(text)
    copied.value = id
    if (copyTimer) clearTimeout(copyTimer)
    copyTimer = setTimeout(() => {
      copied.value = null
      copyTimer = null
    }, 2000)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Copy failed:', err)
    copied.value = null
    if (copyTimer) {
      clearTimeout(copyTimer)
      copyTimer = null
    }
    copyError.value = id
    if (copyErrorTimer) clearTimeout(copyErrorTimer)
    copyErrorTimer = setTimeout(() => {
      copyError.value = null
      copyErrorTimer = null
    }, 2000)
  }
}

onUnmounted(() => {
  if (copyTimer) clearTimeout(copyTimer)
  if (copyErrorTimer) clearTimeout(copyErrorTimer)
})
</script>

<template>
  <section id="get-started" class="py-20 bg-base-200">
    <div class="max-w-5xl mx-auto px-4">
      <ScrollAnimate as="div" class="text-center max-w-3xl mx-auto mb-10">
        <h2 class="text-3xl md:text-4xl font-bold text-base-content">{{ t('getStarted.title') }}</h2>
        <p class="text-lg text-base-content/70 mt-4">
          {{ t('getStarted.subtitle') }}
        </p>
      </ScrollAnimate>

      <ScrollAnimate as="div" class="card bg-base-100 border border-base-300 shadow-md">
        <div class="card-body p-4 md:p-6">
          <div role="tablist" class="tabs tabs-lift">
            <button
              v-for="tab in tabs"
              :id="tabId(tab.id)"
              :key="tab.id"
              role="tab"
              class="tab"
              :class="{ 'tab-active': active === tab.id }"
              :aria-selected="active === tab.id"
              :aria-controls="panelId(tab.id)"
              @click="active = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="mt-4">
            <div
              v-for="tab in tabs"
              :id="panelId(tab.id)"
              :key="tab.id"
              role="tabpanel"
              :aria-labelledby="tabId(tab.id)"
              v-show="active === tab.id"
            >
              <template v-if="tab.id === 'desktop'">
                <figure class="mb-4">
                  <img
                    src="/desktop-client.png"
                    :alt="t('getStarted.desktopAlt')"
                    class="w-full max-w-full h-auto rounded-lg shadow-lg"
                  />
                </figure>
                <div class="text-center">
                  <a
                    href="https://github.com/Wolido/OpenAaaS/releases"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-primary"
                  >
                    <Icon name="download" iconClass="w-5 h-5" />
                    {{ t('getStarted.desktopButton') }}
                  </a>
                </div>
              </template>
              <div v-else>
                <div v-if="tab.id === 'pi'" class="mb-4 space-y-3">
                  <div class="chat chat-end">
                    <div class="chat-bubble bg-base-200 text-base-content">
                      {{ t('getStarted.chatUser') }}
                    </div>
                  </div>
                  <div class="chat chat-start">
                    <div class="chat-bubble chat-bubble-primary text-primary-content">
                      {{ t('getStarted.chatAgent') }}
                    </div>
                  </div>
                  <p class="text-sm text-base-content/60 text-center">
                    {{ t('getStarted.chatHint') }}
                  </p>
                </div>
                <div class="relative">
                  <div class="mockup-code bg-base-300 text-base-content">
                    <pre class="pl-4"><code v-if="tab.id === 'python'" v-html="pythonDisplay"></code><code v-else>{{ snippets[tab.id as SnippetKey] }}</code></pre>
                  </div>
                  <button
                    class="btn btn-sm btn-ghost absolute top-3 right-3 z-10"
                    :aria-label="copyAriaLabel(tab.id)"
                    @click="copy(tab.id as SnippetKey)"
                  >
                    <Icon
                      :name="copied === tab.id ? 'check' : copyError === tab.id ? 'x' : 'copy'"
                      :iconClass="copied === tab.id ? 'w-4 h-4 text-success' : copyError === tab.id ? 'w-4 h-4 text-error' : 'w-4 h-4'"
                    />
                    {{ copyButtonText(tab.id) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollAnimate>
    </div>
  </section>
</template>

<style scoped>
/* 移除 daisyUI mockup-code 为 <pre> 预留的 2ch 前缀空隙，
   避免只有第一行出现多余缩进。 */
.mockup-code pre::before {
  margin-right: 0;
}
</style>
